import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { pathWithoutQuery } from "../../boundaries/url-path.parser";
import type { IssuePageMatch } from "../../enums/scan";
import { ISSUE_PAGE_MATCH } from "../../enums/scan";

type OverviewPage = OverviewResponse["pages"][number];
export type IssuePageResolution = {
  link: Issue["pages"][number];
  page: OverviewPage | null;
  match: IssuePageMatch;
};
export type CurrentIssuePageGroup = {
  page: OverviewPage;
  resolutions: Array<IssuePageResolution & { page: OverviewPage }>;
};

/** The one issue-link↔page predicate: id, exact path, or path sans query. */
export function issueLinkMatchesPage(
  link: Issue["pages"][number],
  page: { id: string; path: string },
): boolean {
  return (
    link.pageId === page.id ||
    link.path === page.path ||
    pathWithoutQuery(link.path) === pathWithoutQuery(page.path)
  );
}

/** Whether a path is in the important set, tolerating query-string variants. */
export function isImportantPath(
  important: ReadonlySet<string>,
  path: string,
): boolean {
  return important.has(path) || important.has(pathWithoutQuery(path));
}

export function resolveIssuePages(
  data: OverviewResponse,
  issue: Issue,
): IssuePageResolution[] {
  const byId = new Map(data.pages.map((page) => [page.id, page]));
  const byPath = new Map(data.pages.map((page) => [page.path, page]));
  const byPathWithoutQuery = new Map(
    data.pages.map((page) => [pathWithoutQuery(page.path), page]),
  );

  return issue.pages.map((link) => {
    const idMatch = byId.get(link.pageId);
    if (idMatch) {
      return { link, page: idMatch, match: "id" };
    }
    const pathMatch = byPath.get(link.path);
    if (pathMatch) {
      return { link, page: pathMatch, match: "path" };
    }
    const pathWithoutQueryMatch = byPathWithoutQuery.get(
      pathWithoutQuery(link.path),
    );
    return pathWithoutQueryMatch
      ? {
          link,
          page: pathWithoutQueryMatch,
          match: "path_without_query",
        }
      : { link, page: null, match: "missing" };
  });
}

export function usesDemoPageFallback(
  resolutions: IssuePageResolution[],
): boolean {
  return resolutions.some((row) => row.match !== ISSUE_PAGE_MATCH.ID);
}

export function uniqueCurrentPagesForIssue(
  data: OverviewResponse,
  issue: Issue,
): OverviewPage[] {
  return currentIssuePageGroups(data, issue).map((group) => group.page);
}

export function currentIssuePageGroups(
  data: OverviewResponse,
  issue: Issue,
): CurrentIssuePageGroup[] {
  const groups = new Map<string, CurrentIssuePageGroup>();
  for (const resolution of resolveIssuePages(data, issue)) {
    const page = resolution.page;
    if (!page) continue;
    const currentResolution = { ...resolution, page };
    const group = groups.get(page.id);
    if (group) {
      group.resolutions.push(currentResolution);
    } else {
      groups.set(page.id, { page, resolutions: [currentResolution] });
    }
  }
  return [...groups.values()];
}
